import React, { useEffect, useRef, useState } from "react";
import api, { extractError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ScanLine, Search, UserPlus, CheckCircle2, AlertTriangle, RotateCcw, PauseCircle,
  Loader2, CalendarDays, Users, Clock, IdCard, Camera,
} from "lucide-react";
import { dataItalianaLunga, oraDa } from "@/components/accessi/utils";
import { CameraScanDialog } from "@/components/accessi/CameraScanDialog";

const detectCf = (raw) => /^[A-Za-z0-9]{11,16}$/.test((raw || "").trim());

const NewPersonDialog = ({ open, onOpenChange, prefill, gruppi, onCreated }) => {
  const { can } = useAuth();
  const canAffiliate = can("accessi.manage");
  const empty = { cognome: "", nome: "", codice_fiscale: "", telefono_principale: "", numero_tessera: "", gruppo: "" };
  const [f, setF] = useState(empty);
  const [creaAff, setCreaAff] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setF({ ...empty, codice_fiscale: detectCf(prefill) ? (prefill || "").toUpperCase() : "",
        cognome: detectCf(prefill) ? "" : (prefill || "") });
      setCreaAff(true);
    }
  }, [open, prefill]);

  const save = async (force = false) => {
    if (!f.cognome.trim()) { toast.error("Inserisci almeno il cognome"); return; }
    setSaving(true);
    try {
      const payload = {
        cognome: f.cognome.trim(), nome: f.nome.trim() || null,
        codice_fiscale: f.codice_fiscale.trim() || null,
        telefono_principale: f.telefono_principale.trim() || null,
        numero_tessera: f.numero_tessera.trim() || null,
      };
      const { data: person } = await api.post(`/persons?force=${force}`, payload);
      if (canAffiliate && creaAff) {
        try {
          await api.post("/accessi/affiliazioni", {
            person_id: person.id, anno: new Date().getFullYear(), stato: "attiva",
            gruppo: f.gruppo || null, numero_tessera: f.numero_tessera.trim() || null,
          });
        } catch (e) { console.error("affiliazione", e); }
      }
      toast.success("Anagrafica creata");
      onOpenChange(false);
      onCreated(person, f.codice_fiscale.trim());
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.potenziali_duplicati) {
        toast.message("Possibile duplicato gi\u00e0 presente", {
          description: "Esiste una scheda con dati simili. Premi di nuovo per creare comunque.",
          action: { label: "Crea comunque", onClick: () => save(true) },
        });
      } else { toast.error(extractError(e)); }
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="accessi-new-person-dialog">
        <DialogHeader><DialogTitle>Nuova anagrafica / affiliazione</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>Cognome *</Label><Input value={f.cognome} onChange={(e) => setF({ ...f, cognome: e.target.value })} data-testid="newp-cognome" /></div>
          <div><Label>Nome</Label><Input value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} data-testid="newp-nome" /></div>
          <div className="sm:col-span-2"><Label>Codice fiscale</Label><Input value={f.codice_fiscale} onChange={(e) => setF({ ...f, codice_fiscale: e.target.value.toUpperCase() })} data-testid="newp-cf" /></div>
          <div><Label>Telefono</Label><Input value={f.telefono_principale} onChange={(e) => setF({ ...f, telefono_principale: e.target.value })} data-testid="newp-tel" /></div>
          <div><Label>N. tessera</Label><Input value={f.numero_tessera} onChange={(e) => setF({ ...f, numero_tessera: e.target.value })} data-testid="newp-tessera" /></div>
          {canAffiliate && (
            <div className="sm:col-span-2 rounded-lg border p-3 bg-slate-50">
              <div className="flex items-center justify-between">
                <Label htmlFor="crea-aff" className="cursor-pointer">Crea affiliazione attiva {new Date().getFullYear()}</Label>
                <Switch id="crea-aff" checked={creaAff} onCheckedChange={setCreaAff} data-testid="newp-crea-affiliazione" />
              </div>
              {creaAff && (
                <div className="mt-2">
                  <Label>Scuola / Gruppo</Label>
                  <Select value={f.gruppo || undefined} onValueChange={(v) => setF({ ...f, gruppo: v })}>
                    <SelectTrigger data-testid="newp-gruppo"><SelectValue placeholder="Seleziona gruppo" /></SelectTrigger>
                    <SelectContent>{gruppi.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="newp-cancel">Annulla</Button>
          <Button onClick={() => save(false)} disabled={saving}
            className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="newp-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />} Crea
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const Registrazione = ({ evento, gruppi, onRefreshEvento, goToEventi }) => {
  const { can } = useAuth();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState(null);
  const [selected, setSelected] = useState(null);
  const [gruppoScelto, setGruppoScelto] = useState("");
  const [working, setWorking] = useState(false);
  const [notFoundOpen, setNotFoundOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const inputRef = useRef(null);
  const debRef = useRef(null);

  const focusInput = () => { setTimeout(() => inputRef.current?.focus(), 50); };
  useEffect(() => { if (evento && !selected) focusInput(); }, [evento, selected]);

  const pickGruppo = (m) => {
    const gs = m.gruppi || [];
    if (evento?.gruppo && gs.includes(evento.gruppo)) return evento.gruppo;
    if (gs.length) return gs[0];
    return evento?.gruppo || "";
  };

  const selectPerson = (m) => { setSelected(m); setMatches(null); setGruppoScelto(pickGruppo(m)); };

  const doIdentify = async (raw) => {
    const v = (raw || "").trim();
    if (v.length < 2) return;
    if (debRef.current) { clearTimeout(debRef.current); debRef.current = null; }
    setLoading(true);
    try {
      const params = { evento_id: evento?.id };
      if (detectCf(v)) params.cf = v; else params.q = v;
      let { data } = await api.get("/accessi/identifica", { params });
      if ((data.count === 0) && params.cf) {
        const r2 = await api.get("/accessi/identifica", { params: { evento_id: evento?.id, q: v } });
        data = r2.data;
      }
      if (data.matches.length === 1) selectPerson(data.matches[0]);
      else if (data.matches.length === 0) { setSelected(null); setMatches([]); setNotFoundOpen(true); }
      else { setSelected(null); setMatches(data.matches); }
    } catch (e) { toast.error(extractError(e)); } finally { setLoading(false); }
  };

  const onChange = (val) => {
    setQuery(val);
    if (debRef.current) clearTimeout(debRef.current);
    if (val.trim().length >= 3 && /[a-zA-Z\s]/.test(val) && !detectCf(val)) {
      debRef.current = setTimeout(() => doIdentify(val), 450);
    }
  };

  const onKeyDown = (e) => { if (e.key === "Enter") { e.preventDefault(); doIdentify(query); } };

  const reset = () => { setQuery(""); setMatches(null); setSelected(null); focusInput(); };

  const registra = async (esito, forza = false) => {
    if (!evento) { toast.error("Seleziona prima un evento"); return; }
    if (!selected) return;
    setWorking(true);
    try {
      const { data } = await api.post("/accessi/registra", {
        evento_id: evento.id, person_id: selected.persona.id, esito,
        gruppo: gruppoScelto || null, forza,
      });
      if (esito === "confermato") toast.success("Accesso confermato");
      else toast.message("Verifica sospesa", { description: "Non conteggiata tra le presenze." });
      onRefreshEvento(data.conteggi);
      reset();
    } catch (e) {
      if (e?.response?.status === 409) {
        const msg = e?.response?.data?.detail?.message || "Accesso gi\u00e0 registrato";
        toast.error(msg);
      } else { toast.error(extractError(e)); }
    } finally { setWorking(false); }
  };

  if (!evento) {
    return (
      <Card className="p-8 text-center" data-testid="accessi-no-evento">
        <div className="h-14 w-14 mx-auto rounded-2xl bg-[color:var(--brand-orange-100)] flex items-center justify-center mb-3">
          <CalendarDays className="h-7 w-7 text-[color:var(--brand-orange-600)]" />
        </div>
        <p className="font-display text-lg font-semibold text-slate-900">Nessun evento selezionato</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">Seleziona o crea un evento/sessione per iniziare a registrare gli accessi.</p>
        <Button onClick={goToEventi} className="mt-4 bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="accessi-vai-eventi">
          <CalendarDays className="h-4 w-4 mr-2" /> Vai agli eventi
        </Button>
      </Card>
    );
  }

  const val = selected?.valutazione;
  const ok = val?.esito === "ok";
  const gia = selected?.gia_registrato;

  return (
    <div className="space-y-4">
      {/* Barra evento + contatori */}
      <Card className="p-4 min-w-0" data-testid="accessi-evento-bar">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="font-display text-lg font-semibold text-slate-900 truncate" data-testid="accessi-evento-nome">{evento.nome_evento}</div>
            <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5">
              <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {dataItalianaLunga(evento.data_evento)}</span>
              {evento.gruppo && <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {evento.gruppo}</span>}
              {evento.ora_inizio && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {evento.ora_inizio}</span>}
              <span className="text-slate-400">Anno rif. {evento.anno_riferimento}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="rounded-lg border px-3 py-1.5 text-center bg-[color:var(--brand-green-100)]" data-testid="accessi-conteggio-presenze">
              <div className="text-xl font-bold text-[color:var(--brand-green-700)]">{evento.conteggi?.presenze ?? 0}</div>
              <div className="text-[10px] uppercase tracking-wide text-[color:var(--brand-green-700)]">Presenze</div>
            </div>
            <div className="rounded-lg border px-3 py-1.5 text-center bg-[color:var(--brand-orange-100)]" data-testid="accessi-conteggio-sospesi">
              <div className="text-xl font-bold text-[color:var(--brand-orange-600)]">{evento.conteggi?.sospesi ?? 0}</div>
              <div className="text-[10px] uppercase tracking-wide text-[color:var(--brand-orange-600)]">In sospeso</div>
            </div>
            <Button variant="outline" size="sm" onClick={goToEventi} data-testid="accessi-cambia-evento">Cambia</Button>
          </div>
        </div>
      </Card>

      {/* Input identificazione */}
      <Card className="p-5 min-w-0">
        <Label className="font-display text-base font-semibold text-slate-900" htmlFor="accessi-identifica-input">IDENTIFICA ASSOCIATO</Label>
        <p className="text-xs text-muted-foreground mt-0.5 mb-3">Scansiona il codice fiscale, digitalo oppure cerca per cognome e nome.</p>
        <div className="flex gap-2">
          <div className="relative flex-1 min-w-0">
            <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input ref={inputRef} id="accessi-identifica-input" value={query} autoFocus
              onChange={(e) => onChange(e.target.value)} onKeyDown={onKeyDown}
              placeholder="Codice fiscale o cognome e nome…"
              className="h-14 pl-11 text-lg" data-testid="accessi-identifica-input" />
          </div>
          <Button onClick={() => doIdentify(query)} disabled={loading} className="h-14 px-5 bg-[color:var(--brand-blue-600)] hover:bg-[color:var(--brand-blue-700)] text-white" data-testid="accessi-cerca-btn">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
          </Button>
          <Button onClick={() => setCameraOpen(true)} variant="outline" className="h-14 px-4" title="Scansiona QR con la fotocamera" data-testid="accessi-camera-btn">
            <Camera className="h-5 w-5" />
          </Button>
        </div>
      </Card>

      {/* Lista omonimie */}
      {matches && matches.length > 1 && (
        <Card className="p-4 min-w-0" data-testid="accessi-omonimie">
          <div className="text-sm font-medium mb-2">Più risultati: seleziona la persona corretta</div>
          <div className="space-y-2">
            {matches.map((m) => (
              <button key={m.persona.id} onClick={() => selectPerson(m)}
                className="w-full text-left rounded-lg border p-3 hover:bg-slate-50 transition-colors flex items-center justify-between gap-2"
                data-testid={`accessi-omonimia-${m.persona.id}`}>
                <span className="min-w-0">
                  <span className="block font-medium truncate">{m.persona.nome_completo}</span>
                  <span className="block text-xs text-muted-foreground truncate">{m.persona.codice_fiscale || "CF non presente"} · {m.persona.telefono || "-"}</span>
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${m.valutazione.esito === "ok" ? "bg-[color:var(--brand-green-100)] text-[color:var(--brand-green-700)]" : "bg-[color:var(--brand-red-100)] text-[color:var(--brand-red-600)]"}`}>
                  {m.valutazione.esito === "ok" ? "Regolare" : "Verifica"}
                </span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Scheda riconoscimento */}
      {selected && (
        <Card className="p-5 min-w-0" data-testid="accessi-scheda">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Associato</div>
              <div className="font-display text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mt-1 break-words" data-testid="accessi-persona-nome">
                {(selected.persona.cognome || "").toUpperCase()} {selected.persona.nome || ""}
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div><dt className="text-muted-foreground text-xs">Codice fiscale</dt><dd className="font-medium break-all">{selected.persona.codice_fiscale || "-"}</dd></div>
                <div><dt className="text-muted-foreground text-xs">Telefono</dt><dd className="font-medium">{selected.persona.telefono || "-"}</dd></div>
                <div><dt className="text-muted-foreground text-xs">N. tessera</dt><dd className="font-medium">{val?.numero_tessera || selected.persona.numero_tessera || "-"}</dd></div>
                <div><dt className="text-muted-foreground text-xs">Anno affiliazione</dt><dd className="font-medium">{val?.anno}</dd></div>
              </dl>
              {(selected.gruppi?.length > 1) ? (
                <div className="mt-3">
                  <Label className="text-xs">Scuola / Gruppo per questo accesso</Label>
                  <Select value={gruppoScelto || undefined} onValueChange={setGruppoScelto}>
                    <SelectTrigger data-testid="accessi-gruppo-select"><SelectValue placeholder="Seleziona gruppo" /></SelectTrigger>
                    <SelectContent>{(selected.gruppi.length ? selected.gruppi : gruppi).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="mt-3 text-sm"><span className="text-muted-foreground text-xs">Scuola / Gruppo: </span><span className="font-medium">{gruppoScelto || "-"}</span></div>
              )}
            </div>

            <div className="min-w-0 flex flex-col">
              {ok ? (
                <div className="rounded-xl border-2 border-[color:var(--brand-green-600)] bg-[color:var(--brand-green-100)] p-5 flex items-center gap-3" data-testid="accessi-semaforo-ok">
                  <CheckCircle2 className="h-12 w-12 text-[color:var(--brand-green-700)] shrink-0" />
                  <div><div className="font-display text-2xl sm:text-3xl font-bold text-[color:var(--brand-green-700)]">OK BENVENUTO</div>
                    <div className="text-sm text-[color:var(--brand-green-700)]">Affiliazione regolare per l'anno {val?.anno}</div></div>
                </div>
              ) : (
                <div className="accessi-blink rounded-xl border-2 border-[color:var(--brand-red-600)] bg-[color:var(--brand-red-100)] p-5 flex items-center gap-3" data-testid="accessi-semaforo-verifica">
                  <AlertTriangle className="h-12 w-12 text-[color:var(--brand-red-600)] shrink-0" />
                  <div><div className="font-display text-2xl sm:text-3xl font-bold text-[color:var(--brand-red-600)]">VERIFICA NECESSARIA</div>
                    <div className="text-sm text-[color:var(--brand-red-600)]" data-testid="accessi-motivo">{val?.motivo || "Controllo richiesto"}</div></div>
                </div>
              )}

              {gia?.registrato && (
                <div className="mt-3 rounded-lg bg-[color:var(--brand-blue-100)] text-[color:var(--brand-blue-700)] px-3 py-2 text-sm flex items-center gap-2" data-testid="accessi-gia-registrato">
                  <Clock className="h-4 w-4" /> ACCESSO GIÀ REGISTRATO - ORE {oraDa(gia.ora)}
                </div>
              )}

              <div className="mt-auto pt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Button onClick={() => registra("confermato")} disabled={working || gia?.registrato}
                  className="h-16 text-base font-semibold bg-[color:var(--brand-green-600)] hover:bg-[color:var(--brand-green-700)] text-white" data-testid="accessi-conferma-btn">
                  {working ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <CheckCircle2 className="h-5 w-5 mr-2" />} CONFERMA
                </Button>
                <Button onClick={() => registra("sospeso")} disabled={working} variant="outline"
                  className="h-16 text-base font-semibold border-[color:var(--brand-orange-600)] text-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-100)]" data-testid="accessi-sospendi-btn">
                  <PauseCircle className="h-5 w-5 mr-2" /> SOSPENDI
                </Button>
                <Button onClick={reset} disabled={working} variant="secondary" className="h-16 text-base font-semibold" data-testid="accessi-prossimo-btn">
                  <RotateCcw className="h-5 w-5 mr-2" /> PROSSIMO
                </Button>
              </div>
              {gia?.registrato && can("accessi.manage") && (
                <Button onClick={() => registra("confermato", true)} disabled={working} variant="ghost" size="sm" className="mt-2 text-xs text-muted-foreground" data-testid="accessi-forza-btn">
                  Registra comunque (correzione autorizzata)
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Non trovato */}
      <Dialog open={notFoundOpen} onOpenChange={setNotFoundOpen}>
        <DialogContent className="max-w-sm" data-testid="accessi-nonTrovato-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><IdCard className="h-5 w-5 text-[color:var(--brand-orange-600)]" /> Associato non trovato</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Nessun associato corrisponde a “{query}”. Vuoi creare una nuova anagrafica?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNotFoundOpen(false); reset(); }} data-testid="accessi-nonTrovato-annulla">Annulla e prosegui</Button>
            {can("persons.write") && (
              <Button onClick={() => { setNotFoundOpen(false); setNewOpen(true); }}
                className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="accessi-nonTrovato-nuova">
                <UserPlus className="h-4 w-4 mr-2" /> Nuova anagrafica
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NewPersonDialog open={newOpen} onOpenChange={setNewOpen} prefill={query} gruppi={gruppi}
        onCreated={(person, cf) => { doIdentify(cf || person.cognome || ""); }} />

      <CameraScanDialog open={cameraOpen} onOpenChange={setCameraOpen}
        onResult={(txt) => { setQuery(txt); doIdentify(txt); }} />
    </div>
  );
};
